
export const WS_LOG_ENABLED = process.env.NODE_ENV === 'development';

export const wsLogger = {
  incoming: (event, data) => {
    if (!WS_LOG_ENABLED) return;
    console.groupCollapsed(`[WS ←] ${event}`);
    console.log('Данные:', data);
    console.groupEnd();
  },
  outgoing: (event, payload) => {
    if (!WS_LOG_ENABLED) return;
    console.groupCollapsed(`[WS →] ${event}`);
    console.log('Отправка:', payload);
    console.groupEnd();
  },
  error: (error, context) => {
    if (!WS_LOG_ENABLED) return;
    console.error(`[WS ✗] Ошибка в ${context}:`, error);
  },
  info: (message, data) => {
    if (!WS_LOG_ENABLED) return;
    console.log(`[WS ℹ] ${message}`, data || '');
  }
};

export class ChatWebSocket {
  constructor(url, handlers) {
    this.url = url;
    this.handlers = handlers;
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimeout = null;
    this.isClosing = false;
  }

  connect() {
    if (!this.url) return;

    if (this.isConnected || 
        (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.CLOSING)) ||
        this.isClosing) {
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.isClosing = false;
    
    try {
      wsLogger.info('Создание нового WebSocket соединения', { url: this.url });
      this.ws = new WebSocket(this.url);
      this.setupEventListeners();
    } catch (error) {
      wsLogger.error(error, 'Создание WebSocket');
      this.scheduleReconnect();
    }
  }

  setupEventListeners() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      wsLogger.info('WebSocket успешно подключен', { url: this.url });
      if (this.handlers.onOpen) this.handlers.onOpen();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        wsLogger.incoming('Получено сообщение от сервера', data);
        if (this.handlers.onMessage) this.handlers.onMessage(data);
      } catch (err) {
        wsLogger.error(err, 'Обработка WebSocket сообщения');
        if (this.handlers.onError) this.handlers.onError(err);
      }
    };

    this.ws.onclose = (event) => {
      this.isConnected = false;
      this.isClosing = false;
      wsLogger.info('WebSocket отключен', { 
        url: this.url, 
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      
      if (this.handlers.onClose) this.handlers.onClose(event);
      
      if (!event.wasClean && event.code !== 1000 && !this.isClosing) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      wsLogger.error(error, 'Ошибка WebSocket');
      if (this.handlers.onError) this.handlers.onError(error);
    };
  }

  send(payload) {
    if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
      const stringPayload = typeof payload === 'string' ? payload : JSON.stringify(payload);
      this.ws.send(stringPayload);
      wsLogger.outgoing('Отправка сообщения', payload);
      return true;
    }
    wsLogger.error('WebSocket не подключен', 'send');
    return false;
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      wsLogger.info('Превышено максимальное количество попыток переподключения');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    wsLogger.info(`Попытка переподключения ${this.reconnectAttempts}/${this.maxReconnectAttempts} через ${delay}ms`);
    
    this.reconnectTimeout = setTimeout(() => {
      if (!this.isConnected && !this.isClosing) {
        this.connect();
      }
    }, Math.min(delay, 30000));
  }

  disconnect() {
    this.isClosing = true;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'Нормальное закрытие');
      }
    }
  }
}


let wsManagerInstance = null;

export class WebSocketManager {
  constructor() {
    if (wsManagerInstance) {
      return wsManagerInstance;
    }
    
    this.allChatsSocket = null;
    this.chatSockets = new Map();
    this.allChatsToken = null;
    this.allChatsHandlers = null;
    this.isAllChatsConnecting = false;
    
    
    this.typingStatus = {};
    this.onlineStatus = {};
    this.lastMessageUpdates = {};
    
    
    this.onTypingUpdate = null;
    this.onOnlineUpdate = null;
    this.onLastMessageUpdate = null;
    this.onNewMessage = null;
    this.onChatUpdated = null;
    
    wsManagerInstance = this;
  }

  static getInstance() {
    if (!wsManagerInstance) {
      wsManagerInstance = new WebSocketManager();
    }
    return wsManagerInstance;
  }

  
  setCallbacks(callbacks) {
    this.onTypingUpdate = callbacks.onTypingUpdate;
    this.onOnlineUpdate = callbacks.onOnlineUpdate;
    this.onLastMessageUpdate = callbacks.onLastMessageUpdate;
    this.onNewMessage = callbacks.onNewMessage;
    this.onChatUpdated = callbacks.onChatUpdated;
  }

  
  updateTypingStatus(chatId, isTyping, userType, userId) {
    if (!chatId) return;
    const timestamp = new Date().toISOString();
    this.typingStatus[chatId] = { 
      isTyping, 
      userType, 
      userId,
      timestamp
    };
    
    if (this.onTypingUpdate) {
      this.onTypingUpdate(chatId, this.typingStatus[chatId]);
    }
    
    
    if (isTyping) {
      setTimeout(() => {
        if (this.typingStatus[chatId]?.timestamp === timestamp) {
          this.typingStatus[chatId] = { ...this.typingStatus[chatId], isTyping: false };
          if (this.onTypingUpdate) {
            this.onTypingUpdate(chatId, this.typingStatus[chatId]);
          }
        }
      }, 5000);
    }
  }

  
  updateOnlineStatus(chatId, userId, userType, isConnected) {
    if (!chatId || !userId) return;
    
    if (!this.onlineStatus[chatId]) {
      this.onlineStatus[chatId] = {};
    }
    
    this.onlineStatus[chatId][userId] = { userType, connected: isConnected };
    
    if (this.onOnlineUpdate) {
      this.onOnlineUpdate(chatId, { ...this.onlineStatus[chatId] });
    }
  }

  
  updateLastMessage(chatId, message, messageType, timestamp, senderType) {
    if (!chatId) return;
    
    this.lastMessageUpdates[chatId] = {
      last_message: message,
      last_message_type: messageType,
      last_message_time: timestamp,
      last_sender_type: senderType
    };
    
    if (this.onLastMessageUpdate) {
      this.onLastMessageUpdate(chatId, this.lastMessageUpdates[chatId]);
    }
  }

  
  sendTypingIndicator(chatId, isTyping, userType) {
    const socket = this.chatSockets.get(chatId);
    if (socket && socket.isConnected) {
      const payload = {
        type: "typing",
        is_typing: isTyping,
        user_type: userType,
        chat_id: chatId
      };
      return socket.send(payload);
    }
    return false;
  }

  
  createAllChatsHandler(queryClient, getSenderType, processImageData) {
    return (data) => {
        switch (data.type) {
        case 'connected':
            wsLogger.info('Успешно подключились ко всем чатам', data);
            break;
            
        case 'chat_typing':
            if (data.chat_id && data.user_type) {
            this.updateTypingStatus(data.chat_id, data.is_typing, data.user_type, data.user_id);
            }
            break;
            
        case 'chat_message':
            if (data.status === "DELIVERED" || data.event === "new_message") {
            if (this.chatSockets.has(data.chat_id)) {
                break; 
            }
            if (this.onMessageStatusUpdate) {
                this.onMessageStatusUpdate({
                chat_id: data.chat_id,
                client_message_id: data.client_message_id || data.message_id,
                server_message_id: data.message_id,
                status: data.status,
                timestamp: data.timestamp
                });
            }
            
            
            this.updateLastMessage(
                data.chat_id,
                data.content,
                data.message_type,
                data.timestamp,
                data.sender_type
            );
            }
            break;
          
        case 'chat_updated':
          if (this.onChatUpdated) {
            this.onChatUpdated(data.chat_id);
          }
          break;
          
        case 'user_connected':
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(data.chat_id, data.user_id, data.user_type, true);
          }
          break;
          
        case 'user_disconnected':
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(data.chat_id, data.user_id, data.user_type, false);
          }
          break;
          
        case 'message_updated':
          
          break;
          
        default:
          break;
      }
    };
  }

  
  createChatHandler(chatId, queryClient, getSenderType, processImageData) {
    return (data) => {
      switch (data.type) {
        case 'connected':
          break;
          
        case 'user_connected':
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(data.chat_id, data.user_id, data.user_type, true);
          }
          break;
          
        case 'user_disconnected':
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(data.chat_id, data.user_id, data.user_type, false);
          }
          break;
          
        case 'message':
          if (data.status === "DELIVERED") {
            if (this.onNewMessage) {
              const message = {
                id: data.message_id || data.id,
                chat_id: data.chat_id,
                sender_type: data.sender_type,
                content: data.message_type === "IMAGE" ? "" : data.content,
                message_type: data.message_type || "TEXT",
                status: data.status || "SENT",
                created_at: data.timestamp || new Date().toISOString(),
                updated_at: data.timestamp || new Date().toISOString(),
                source: "WEB",
                image_url: data.message_type === "IMAGE" ? processImageData(data.content) : null
              };
              this.onNewMessage(message);
            }
            
            
            this.updateLastMessage(
              data.chat_id,
              data.content,
              data.message_type,
              data.timestamp,
              data.sender_type
            );
            
            
            this.updateTypingStatus(data.chat_id, false, data.sender_type, data.user_id);
          }
          break;
          
        case 'typing':
          if (data.chat_id && data.user_type) {
            this.updateTypingStatus(data.chat_id, data.is_typing, data.user_type, data.user_id);
          }
          break;
          
        default:
          break;
      }
    };
  }

  connectToAllChats(token, handlers) {
    if (!token) {
      wsLogger.error('Токен не предоставлен для подключения ко всем чатам', 'connectToAllChats');
      return null;
    }

    if (this.allChatsToken === token && this.allChatsSocket) {
      if (handlers !== this.allChatsHandlers) {
        wsLogger.info('Обновляем обработчики для существующего /all/ сокета');
        this.allChatsSocket.handlers = handlers;
        this.allChatsHandlers = handlers;
      }

      if (!this.allChatsSocket.isConnected && 
          !this.allChatsSocket.isClosing && 
          !this.isAllChatsConnecting) {
        wsLogger.info('Переподключаем /all/ сокет');
        this.allChatsSocket.connect();
      }

      return this.allChatsSocket;
    }

    if (this.allChatsSocket) {
      wsLogger.info('Закрываем старое /all/ соединение (изменился токен)');
      this.allChatsSocket.disconnect();
      this.allChatsSocket = null;
      this.allChatsToken = null;
      this.allChatsHandlers = null;
    }

    const url = `wss://dev.tablecrm.com/api/v1/chats/ws/all/?token=${token}`;
    
    if (this.isAllChatsConnecting) {
      wsLogger.info('Подключение ко всем чатам уже в процессе');
      return null;
    }

    this.isAllChatsConnecting = true;
    this.allChatsToken = token;
    this.allChatsHandlers = handlers;
    
    this.allChatsSocket = new ChatWebSocket(url, {
      ...handlers,
      onOpen: () => {
        this.isAllChatsConnecting = false;
        if (handlers.onOpen) handlers.onOpen();
      },
      onClose: (event) => {
        this.isAllChatsConnecting = false;
        if (event.code !== 1000) {
          this.allChatsSocket = null;
          this.allChatsToken = null;
          this.allChatsHandlers = null;
        }
        if (handlers.onClose) handlers.onClose(event);
      },
      onError: (error) => {
        this.isAllChatsConnecting = false;
        if (handlers.onError) handlers.onError(error);
      }
    });
    
    this.allChatsSocket.connect();
    
    return this.allChatsSocket;
  }

  connectToChat(chatId, token, handlers) {
    if (!chatId || !token) return null;

    const url = `wss://dev.tablecrm.com/api/v1/chats/ws/${chatId}/?token=${token}`;

    let socket = this.chatSockets.get(chatId);

    if (socket) {
      wsLogger.info(`Обновляем handlers для существующего сокета чата ${chatId}`);
      socket.handlers = handlers;

      if (!socket.isConnected && !socket.isClosing) {
        socket.connect();
      }

      return socket;
    }

    socket = new ChatWebSocket(url, handlers);
    socket.connect();
    this.chatSockets.set(chatId, socket);
    
    return socket;
  }

  getSocket(chatId) {
    return this.chatSockets.get(chatId);
  }

  disconnectFromChat(chatId) {
    if (this.chatSockets.has(chatId)) {
      const socket = this.chatSockets.get(chatId);
      socket.disconnect();
      this.chatSockets.delete(chatId);
      wsLogger.info(`Отключили WebSocket для чата ${chatId}`);
    }
  }

  disconnectAll() {
    wsLogger.info('Отключаем все WebSocket соединения');
    
    if (this.allChatsSocket) {
      this.allChatsSocket.disconnect();
      this.allChatsSocket = null;
      this.allChatsToken = null;
    }

    this.chatSockets.forEach((socket, chatId) => {
      socket.disconnect();
      wsLogger.info(`Отключили WebSocket для чата ${chatId}`);
    });
    this.chatSockets.clear();
  }

  
  getTypingStatus(chatId) {
    return this.typingStatus[chatId];
  }

  
  getOnlineStatus(chatId) {
    const chatOnline = this.onlineStatus[chatId];
    if (!chatOnline) return false;
    
    return Object.values(chatOnline).some(user => 
      user.userType === 'CLIENT' && user.connected
    );
  }

  
  getLastMessageUpdates(chatId) {
    return this.lastMessageUpdates[chatId];
  }
}